/**
 * The comments a card carries, written once its change has landed.
 *
 * The agent attaches comments to the change it proposes (`comments` on
 * `redline_section`, `edit_table` and `add_paragraph`), and the core places each on a
 * paragraph the card writes (`CardComment`). A card's comments are written only after
 * the card itself has landed, by single Apply and by Apply All alike, so a comment is
 * never in the document without the change it explains.
 */
import { APPLY_FAILURE_LOCATION_NOT_FOUND } from "@common/constants/appliedActions";
import type { CardComment, CardParagraphAnchor, RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import type { CommentCardContent } from "../components/chat/proposal-card/card-content";
import { cardCommentKeyOf } from "../components/chat/proposal-card/card-comment-selections";
import { recoverCommentTargetWithin } from "../components/chat/proposal-card/comment-target-recovery";
import { type CommentRelocation, relocatedCommentSentenceOf } from "./comment-anchors";
import { applyBulkCommentData, type BulkApplyCommentEntry, type CommentEntryFailure, withWordContextWrite } from "./document";
import type { AppliedCommentReport } from "./anchor/document-events";
import { ApplyFailureError, CommentsNotWrittenRedlineApply, type CommentNotWritten, commentNotWrittenCauseOf, quotedParagraphLabelOf } from "./errors";
import { appliedCommentReportOf, commentNotWrittenReportOf, tellAgentsCardsApplied, type WrittenComment } from "./redline-applied-cards";

/**
 * A card comment as the comment writer takes it, credited to the card it rides on.
 *
 * CASES, for `rule_uuids`
 *   - the card has an id (a chat card)    -> [that id], so a failure names the card
 *   - it has none (a composed resolution) -> []
 */
const commentEntryOfCardComment = (cardId: string | null, cardComment: CardComment): BulkApplyCommentEntry => ({
    target_bookmark_names: [cardComment.bookmark],
    comment_text: cardComment.text,
    target_text: "",
    placement: cardComment.placement,
    anchor_text: cardComment.anchor_text,
    rule_uuids: cardId === null ? [] : [cardId],
});

/**
 * The location context of the paragraph a card comment's bookmark names, as the card
 * carries it: the paragraph's text as the card leaves it and its anchor (ids, heading
 * path, index, neighbours), so the comment's paragraph is found again by them when
 * Word dropped the bookmark (`recoverCommentTargetWithin`).
 */
interface CommentedParagraphContext {
    paragraphText: string | null;
    paragraphAnchor: CardParagraphAnchor | null;
}

/** No paragraph of the card under the comment's bookmark: the paragraph is found by its bookmark alone. */
const NO_COMMENTED_PARAGRAPH_CONTEXT: CommentedParagraphContext = { paragraphText: null, paragraphAnchor: null };

/**
 * The location context the card carries for the paragraph under `bookmark`.
 *
 * CASES, for the paragraph the bookmark names
 *   - a paragraph the card edits -> its text as proposed, and its anchor
 *   - a paragraph the card adds  -> its text as proposed, and the anchor the core built for it
 *                                   from the paragraph it follows (`RedlineTextInsertion.anchor`),
 *                                   so it is looked for there and not across the whole document
 *
 * EXAMPLE
 *   bookmark _tile_ember_quote, the card adds "Disputes go to the CFO." under it
 *   -> { paragraphText: "Disputes go to the CFO.", paragraphAnchor: index 9 under ["1. Payment"] }
 */
const commentedParagraphContextOf = (bookmark: string, textRedline: RedlineTextProposal | null): CommentedParagraphContext => {
    /** NULL textRedline: a markup card carries no paragraph texts. */
    if (textRedline === null) return NO_COMMENTED_PARAGRAPH_CONTEXT;
    const paragraphsTheCardWrites = [...textRedline.paragraphs, ...textRedline.insertions];
    const commentedParagraph = paragraphsTheCardWrites.find((cardParagraph) => cardParagraph.bookmark === bookmark);
    /** WHEN the card writes no paragraph under the bookmark (a comment after a paragraph only a table cell names) CONSEQUENCE no text or anchor. */
    if (commentedParagraph === undefined) return NO_COMMENTED_PARAGRAPH_CONTEXT;
    /** NULL anchor: a card stored before anchors were carried; the paragraph is found by its text. */
    return { paragraphText: commentedParagraph.textProposed, paragraphAnchor: commentedParagraph.anchor ?? null };
};

/** A card comment as a comment card, with the location context of its paragraph (`commentedParagraphContextOf`). */
const commentContentOfCardComment = (cardComment: CardComment, textRedline: RedlineTextProposal | null): CommentCardContent => ({
    kind: "comment",
    text: cardComment.text,
    bookmarks: [cardComment.bookmark],
    anchorText: cardComment.anchor_text,
    placement: cardComment.placement,
    ...commentedParagraphContextOf(cardComment.bookmark, textRedline),
});

/**
 * Finds again, once per bookmark, the paragraph of every card comment whose bookmark
 * Word dropped, by the card's location context, and lays the bookmark back on it.
 *
 * RETURNS
 *   The bookmarks whose paragraph is still nowhere to be found: the paragraph is gone.
 *
 * WHY recovery comes before the write
 *   A comment whose paragraph is in the document is always written. Only a paragraph
 *   that is no longer there leaves a comment out.
 *
 * WHY a recovery error is reported, never thrown
 *   The card has already landed when its comments are written. An error that says
 *   nothing about the paragraph (Word busy, a GeneralException) thrown from here would
 *   reach the card's apply as an ordinary failure: the card would read failed, stay
 *   selected, and the next Apply All would write it a second time. The comment is
 *   written by its bookmark instead, and the writer names it if Word refuses it.
 *
 * EXAMPLE
 *   comments on _pay, _pay, _late; _late's paragraph deleted -> recovery runs for _pay and
 *   _late once each -> Set {"_late"}
 */
const bookmarksWhoseParagraphIsGone = async (
    cardComments: CardComment[],
    textRedline: RedlineTextProposal | null,
    sendError?: (error: unknown, context?: Record<string, unknown>) => void,
): Promise<Set<string>> => {
    const paragraphGoneBookmarks = new Set<string>();
    const firstCommentOfEachBookmark = new Map(cardComments.map((cardComment) => [cardComment.bookmark, cardComment] as const));
    await withWordContextWrite(async (context) => {
        for (const [bookmark, cardComment] of firstCommentOfEachBookmark) {
            try {
                await recoverCommentTargetWithin(context, commentContentOfCardComment(cardComment, textRedline));
            } catch (error) {
                /**
                 * CASES
                 *   - recovery found no paragraph for the bookmark -> it is gone; its comments are left out
                 *   - anything else                                -> not a verdict on the paragraph: reported, and
                 *                                                     its comments are written by their bookmark
                 */
                if (error instanceof ApplyFailureError && error.reason === APPLY_FAILURE_LOCATION_NOT_FOUND) paragraphGoneBookmarks.add(bookmark);
                else sendError?.(error, { component: "writeCardComments", action: "recover_comment_target", bookmark });
            }
        }
    }).catch((error: unknown) => sendError?.(error, { component: "writeCardComments", action: "recover_comment_targets" }));
    return paragraphGoneBookmarks;
};

/** The comment the writer could not write, with the reason the card shows for it. */
const commentNotWrittenOf = (failure: CommentEntryFailure): CommentNotWritten => ({
    commentText: failure.commentEntry.comment_text,
    paragraphGone: failure.failureReason === APPLY_FAILURE_LOCATION_NOT_FOUND,
});

/**
 * One of a card's comments the writer relocated, and the two sentences that say where it
 * landed: one for the comment's own sub-card, one for the card or its Details, which name
 * the comment.
 */
export interface RelocatedCardComment {
    /**
     * The comment's own key (`cardCommentKeyOf`), which its sub-card shows the relocation under.
     *
     * NULL
     *   A comment stored before comments carried an id: it has no sub-card of its own, and the
     *   card shows the relocation.
     */
    cardCommentKey: string | null;
    /** EXAMPLE 'Placed on "CONSULTANT": "shall not exceed …" is no longer in this paragraph.' */
    relocationSentence: string;
    /** EXAMPLE 'Comment “Pegging the cap…” placed on "CONSULTANT": "shall not exceed …" is no longer in this paragraph.' */
    relocationSentenceNamingTheComment: string;
}

/**
 * Where a card comment the writer relocated is shown, and how (`relocatedCommentSentenceOf`).
 *
 * EXAMPLE
 *   card "card-7", comment "Pegging the cap to the lesser of …" with id cc_3f9a, landed on
 *   "CONSULTANT", its quote gone
 *   -> { cardCommentKey: "card-7#comment_cc_3f9a", relocationSentence: 'Placed on "CONSULTANT": …',
 *        relocationSentenceNamingTheComment: 'Comment “Pegging the cap to the lesser of…” placed on "CONSULTANT": …' }
 */
export const relocatedCardCommentOf = (cardId: string | null, cardComment: CardComment, relocation: CommentRelocation): RelocatedCardComment => ({
    /** NULL cardId: a composed resolution's comment rides no chat card, so it has no sub-card. */
    cardCommentKey: cardId === null ? null : cardCommentKeyOf(cardId, cardComment),
    relocationSentence: relocatedCommentSentenceOf(relocation, cardComment.anchor_text),
    relocationSentenceNamingTheComment: relocatedCommentSentenceOf(relocation, cardComment.anchor_text, `Comment ${quotedParagraphLabelOf(cardComment.text)} placed`),
});

/** The comment whose relocation a recovery warning marks, and the sentence it shows. */
export interface CardCommentRecoveryWarning {
    /** The comment's own key, or the card's id for a comment with no sub-card. */
    actionId: string;
    warning: string;
}

/**
 * The recovery warning a relocated card comment leaves, on its sub-card or on the card.
 *
 * CASES
 *   - the comment has a key of its own -> on that key, the sentence for its sub-card
 *   - it has none                      -> on the card, the sentence naming the comment
 */
export const recoveryWarningOfRelocatedCardComment = (cardId: string, relocatedCardComment: RelocatedCardComment): CardCommentRecoveryWarning => {
    /** WHEN the comment has no key of its own CONSEQUENCE the card carries the warning, naming the comment. */
    if (relocatedCardComment.cardCommentKey === null) return { actionId: cardId, warning: relocatedCardComment.relocationSentenceNamingTheComment };
    return { actionId: relocatedCardComment.cardCommentKey, warning: relocatedCardComment.relocationSentence };
};

/**
 * The comments a batch wrote, as the agent is told of them: every entry handed to the
 * writer that it did not report as a failure, with the text and quoted words written.
 *
 * WHY the entries, not the card's comments
 *   An entry carries the text the batch wrote, the user's edit of the comment included,
 *   and a failure names the very entry it failed, so the entries not failed are exactly
 *   the comments in the document.
 *
 * EXAMPLE
 *   entries [“Matches the cure period.” on _cure_days, “Audit right added.” on _audit], the second failed
 *   -> [{ cardId msg-7, [_cure_days], “Matches the cure period.”, quotedText "thirty (30) days" }]
 *
 * NULL `quotedText` when the comment sits on a first word or a whole paragraph.
 */
const writtenCommentsOf = (cardId: string | null, handedEntries: BulkApplyCommentEntry[], failures: CommentEntryFailure[]): WrittenComment[] => {
    const failedEntries = new Set(failures.map((failure) => failure.commentEntry));
    return handedEntries
        .filter((entry) => !failedEntries.has(entry))
        .map((entry) => ({ cardId, paragraphBookmarkNames: entry.target_bookmark_names, text: entry.comment_text, quotedText: entry.anchor_text ?? null }));
};

/**
 * The comments a card could not add, as the agent is told of them: each with the reason
 * the card shows, so the agent hears of the comment the card reads as not added.
 *
 * CASES
 *   - a comment whose paragraph is gone     -> "its paragraph is not in the document"
 *   - a comment Word refused (a failure)    -> "Word refused it", or the paragraph reason when
 *                                              the writer found no paragraph for it
 *
 * EXAMPLE
 *   “Audit right added.” on _audit, Word refused it
 *   -> [{ comment, card-7, [_audit], “Audit right added.”, notWrittenReason "Word refused it" }]
 */
const commentsNotWrittenReportsOf = (cardId: string | null, paragraphGoneComments: CardComment[], failures: CommentEntryFailure[]): AppliedCommentReport[] => [
    ...paragraphGoneComments.map((cardComment) => commentNotWrittenReportOf(
        { cardId, paragraphBookmarkNames: [cardComment.bookmark], text: cardComment.text, quotedText: cardComment.anchor_text ?? null },
        commentNotWrittenCauseOf(true),
    )),
    ...failures.map((failure) => commentNotWrittenReportOf(
        { cardId, paragraphBookmarkNames: failure.commentEntry.target_bookmark_names, text: failure.commentEntry.comment_text, quotedText: failure.commentEntry.anchor_text ?? null },
        commentNotWrittenCauseOf(commentNotWrittenOf(failure).paragraphGone),
    )),
];

/**
 * Writes a landed card's comments, each where the core placed it, in one batch. A
 * comment on a paragraph the card adds names that paragraph's own id, which the
 * applier wrote it under, so it is written there like any other.
 *
 * CASES
 *   - the card carries no comment          -> nothing is written
 *   - a comment's bookmark was dropped     -> its paragraph is found again first, by the card's
 *                                             location context, and the comment written there
 *   - a comment written somewhere other than where the core placed it
 *                                          -> `onCommentRelocated` hears of it, before any refusal
 *                                             below, so the comment says where it landed even when
 *                                             another comment of the card was not written
 *   - a comment was written, or was not    -> the agents are told of it as the card's own
 *                                             (`writtenCommentsOf`, `commentsNotWrittenReportsOf`),
 *                                             before any refusal below, so each comment reaches the
 *                                             agent with its card, written or with why it was not,
 *                                             whichever path applied it
 *   - every comment was written            -> nothing more: the card applied as written
 *   - a paragraph is gone, or Word refused -> `CommentsNotWrittenRedlineApply` naming each such
 *                                             comment and why: the card stays applied and reads
 *                                             partly applied, and nothing written is taken back
 *
 * EXAMPLE
 *   comments ["Matches the cure period.", "Audit right added."], the second's paragraph deleted
 *   -> CommentsNotWrittenRedlineApply: Comment “Audit right added.” not added: its paragraph is
 *      not in the document.
 *   comments ["One percent a month.", "Audit right added."], the first relocated, Word refused the second
 *   -> `onCommentRelocated` for the first, then CommentsNotWrittenRedlineApply for the second
 */
export const writeCardComments = async (
    cardId: string | null,
    cardComments: CardComment[],
    textRedline: RedlineTextProposal | null,
    onCommentRelocated: (relocatedCardComment: RelocatedCardComment) => void,
    sendError?: (error: unknown, context?: Record<string, unknown>) => void,
): Promise<void> => {
    /** WHEN the card carries no comment CONSEQUENCE there is nothing to write. */
    if (cardComments.length === 0) return;
    const paragraphGoneBookmarks = await bookmarksWhoseParagraphIsGone(cardComments, textRedline, sendError);
    const cardCommentOfEntry = new Map(cardComments
        .filter((cardComment) => !paragraphGoneBookmarks.has(cardComment.bookmark))
        .map((cardComment) => [commentEntryOfCardComment(cardId, cardComment), cardComment] as const));
    const written = await applyBulkCommentData(Array.from(cardCommentOfEntry.keys()), sendError);
    for (const relocation of written.commentEntryRelocations) {
        const relocatedComment = cardCommentOfEntry.get(relocation.commentEntry);
        /** WHEN the writer names an entry this card did not hand it CONSEQUENCE it is no comment of this card's. */
        if (relocatedComment !== undefined) onCommentRelocated(relocatedCardCommentOf(cardId, relocatedComment, relocation));
    }
    tellAgentsCardsApplied([
        ...writtenCommentsOf(cardId, Array.from(cardCommentOfEntry.keys()), written.commentEntryFailures).map(appliedCommentReportOf),
        ...commentsNotWrittenReportsOf(cardId, cardComments.filter((cardComment) => paragraphGoneBookmarks.has(cardComment.bookmark)), written.commentEntryFailures),
    ]);
    const commentsNotWritten: CommentNotWritten[] = [
        ...cardComments.filter((cardComment) => paragraphGoneBookmarks.has(cardComment.bookmark)).map((cardComment) => ({ commentText: cardComment.text, paragraphGone: true })),
        ...written.commentEntryFailures.map(commentNotWrittenOf),
    ];
    /** WHEN every comment was written CONSEQUENCE the card applied as written. */
    if (commentsNotWritten.length === 0) return;
    throw new CommentsNotWrittenRedlineApply(commentsNotWritten, cardComments.length);
};
